import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type JuiceEvent =
  | 'spin'
  | 'tick'
  | 'cash'
  | 'hit'
  | 'miss'
  | 'bankrupt'
  | 'lose'
  | 'solve'
  | 'win'
  | 'click';

interface JuiceContextType {
  muted: boolean;
  toggleMuted: () => void;
  play: (event: JuiceEvent) => void;
}

const JuiceContext = createContext<JuiceContextType>({
  muted: true,
  toggleMuted: () => undefined,
  play: () => undefined,
});

const STORAGE_KEY = 'spin-solve-muted';

function getAudioContext(): AudioContext | null {
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  const existing = (window as unknown as { __spinSolveAudio?: AudioContext }).__spinSolveAudio;
  if (existing) return existing;
  const ctx = new Ctor();
  (window as unknown as { __spinSolveAudio?: AudioContext }).__spinSolveAudio = ctx;
  return ctx;
}

function tone(
  ctx: AudioContext,
  frequency: number,
  duration: number,
  type: OscillatorType,
  gainValue: number,
  when = 0
) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = type;
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(gainValue, ctx.currentTime + when);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + when + duration);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(ctx.currentTime + when);
  oscillator.stop(ctx.currentTime + when + duration);
}

function playSound(event: JuiceEvent) {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    void ctx.resume();
  }
  switch (event) {
    case 'click':
      tone(ctx, 520, 0.05, 'square', 0.04);
      break;
    case 'spin':
    case 'tick':
      tone(ctx, 180 + Math.random() * 80, 0.06, 'square', 0.05);
      break;
    case 'cash':
      tone(ctx, 523, 0.12, 'triangle', 0.08);
      tone(ctx, 659, 0.16, 'triangle', 0.07, 0.08);
      tone(ctx, 784, 0.22, 'triangle', 0.06, 0.16);
      break;
    case 'hit':
      tone(ctx, 660, 0.1, 'triangle', 0.08);
      tone(ctx, 880, 0.14, 'triangle', 0.06, 0.08);
      break;
    case 'miss':
      tone(ctx, 180, 0.18, 'sawtooth', 0.05);
      break;
    case 'bankrupt':
      tone(ctx, 140, 0.28, 'sawtooth', 0.08);
      tone(ctx, 90, 0.35, 'square', 0.05, 0.08);
      break;
    case 'lose':
      tone(ctx, 240, 0.16, 'triangle', 0.05);
      break;
    case 'solve':
      [523, 659, 784, 1046].forEach((frequency, index) => {
        tone(ctx, frequency, 0.2, 'triangle', 0.07, index * 0.09);
      });
      break;
    case 'win':
      [392, 523, 659, 784, 1046].forEach((frequency, index) => {
        tone(ctx, frequency, 0.22, 'triangle', 0.07, index * 0.1);
      });
      break;
    default:
      break;
  }
}

function haptic(event: JuiceEvent) {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
    return;
  }
  const patterns: Partial<Record<JuiceEvent, number | number[]>> = {
    click: 8,
    spin: [12, 30, 12],
    cash: [20, 40, 30],
    hit: 18,
    miss: [30, 40, 30],
    bankrupt: [80, 40, 80, 40, 120],
    lose: 40,
    solve: [20, 30, 20, 30, 40],
    win: [25, 40, 25, 40, 60],
  };
  const pattern = patterns[event];
  if (pattern) {
    navigator.vibrate(pattern);
  }
}

export const JuiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [muted, setMuted] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
    } catch {
      // ignore
    }
  }, [muted]);

  const toggleMuted = useCallback(() => {
    setMuted((value) => {
      const next = !value;
      if (value) {
        playSound('click');
      }
      return next;
    });
  }, []);

  const play = useCallback(
    (event: JuiceEvent) => {
      haptic(event);
      if (!muted) {
        playSound(event);
      }
    },
    [muted]
  );

  const value = useMemo(() => ({ muted, toggleMuted, play }), [muted, toggleMuted, play]);

  return <JuiceContext.Provider value={value}>{children}</JuiceContext.Provider>;
};

export const useJuice = () => useContext(JuiceContext);
